import React, { useState } from "react";
import { CircularProgress, Grid } from "@mui/material";
import { swalError, swalMessage } from "@unisearch/helpers";
import API from "@unisearch/helpers/api";
import { Field, Form, Formik } from "formik";
import Link from "next/link";
import Select from "react-select";
import * as Yup from "yup";
import styles from "./lead_form.module.scss";

const selectStyles = {
  control: (styles) => ({
    ...styles,
    backgroundColor: "white",
    fontSize: "14px",
    padding: "5px",
    border: "1px solid #ebe9f1 !important",
    boxShadow: "0 !important",
    "&:hover": {
      border: "1px solid #ebe9f1 !important",
    },
  }),
  option: (styles, { isDisabled, isFocused, isSelected }) => {
    const color = "#333";
    return {
      ...styles,
      fontSize: "13px",
      color: isSelected ? "#F47F20" : "#B5B5C3",
      backgroundColor: isSelected
        ? "#F5F6FC"
        : isFocused
        ? "#F5F6FC"
        : styles.backgroundColor,
      "&:hover": {
        backgroundColor: "#F5F6FC",
        color: "#F47F20",
      },
      cursor: isDisabled ? "not-allowed" : "pointer",
    };
  },
  menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
  menu: (provided) => ({ ...provided, zIndex: 9999 }),
  placeholder: (defaultStyles) => {
    return {
      ...defaultStyles,
      color: "#c2c2c2",
      fontSize: "13px",
    };
  },
};

export const LeadFormSection = ({ subject, subjectHTML, bodyHTML }) => {
  const _init = {
    subject,
    subjectHTML,
    bodyHTML,
    fullName: "",
    contactNumber: "",
    email: "",
    cityName: "",
    ieltsModule: "",
    countryName: "",
    currentEducation: "",
    year: "",
    funding: "",
    status: "Active",
    terms_and_condition: false,
  };

  const [initialState, setInitialState] = useState(_init);
  const [loading, setLoading] = useState(false);
  const isFormInable = (values, errors) => {
    if (
      errors &&
      Object.keys(errors).length === 0 &&
      values.fullName &&
      values.email
    ) {
      return false;
    } else {
      return true;
    }
  };

  const ieltsModuleArr = [
    { value: "Academic", label: "Academic" },
    { value: "General", label: "General" },
  ];
  const countryArr = [
    { value: "Australia", label: "Australia" },
    { value: "USA", label: "USA" },
    { value: "UK", label: "UK" },
    { value: "Canada", label: "Canada" },
    { value: "Ireland", label: "Ireland" },
    { value: "New Zealand", label: "New Zealand" },
  ];
  const educationArr = [
    { value: "12th", label: "12th" },
    { value: "UG", label: "UG" },
  ];
  const yearArr = [
    { value: "2022", label: "2022" },
    { value: "2023", label: "2023" },
    { value: "2024", label: "2024" },
  ];
  const fundArr = [
    { value: "Self Funded", label: "Self Funded" },
    { value: "Parents", label: "Parents" },
    { value: "Education Loan", label: "Education Loan" },
  ];

  // form validation rules
  const phoneRegExp = /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;

  const validationSchema = Yup.object().shape({
    fullName: Yup.string().required("Name is required"),
    email: Yup.string()
      .email()
      .typeError("Please provide valid email")
      .required("Email is required"),
    contactNumber: Yup.string()
      .matches(phoneRegExp, "Contact Number number is not valid")
      .min(9)
      .max(15)
      .required("Contact Number is required"),
    cityName: Yup.string().required("City is required"),
    terms_and_condition: Yup.bool().oneOf(
      [true],
      "You must accept the terms and conditions"
    ),
  });

  const handleRegister = async ({ ...values }, actions) => {
    delete values.terms_and_condition;
    setLoading(true);
    await API.post("public/incoming-leads", values).then((resp) => {
      if (resp.error) {
        swalError("Something went wrong");
        setLoading(false);
      }
      if (!resp.error) {
        swalMessage("Welcome!", "You have successfully registered");
        actions.resetForm();
        setLoading(false);
        // swalSuccess("You have successfully registered", "Welcome!");
      }
    });
  };
  return (
    <section id="form">
      <div className="container">
        <div className={styles.form_wrapper}>
          <div className={styles.form_title_wrapper}>
            <h2>
              Interested in Signing Up for Your First IELTS Training Session?
            </h2>
            <p>Just fill in your details and we’ll call you back.</p>
          </div>

          <Formik
            initialValues={initialState}
            enableReinitialize={true}
            validationSchema={validationSchema}
            onSubmit={(values, actions) => {
              handleRegister(values, actions);
            }}
          >
            {({ handleSubmit, values, setFieldValue, errors, touched }) => (
              <Form className="form" autoComplete="off">
                <Grid container columnSpacing={{ xs: 2, sm: 2, md: 4 }}>
                  <Grid item xs={12} sm={12} md={12}>
                    <div className={styles.form_group}>
                      <label>
                        Full Name <span className={styles.asterisk}>*</span>
                      </label>
                      <input
                        id="fullName"
                        name="fullName"
                        placeholder="Full Name"
                        type="text"
                        value={values.fullName ?? ""}
                        onChange={(e) => {
                          setFieldValue("fullName", e.target.value);
                          touched.fullName = true;
                        }}
                      />
                      {errors.fullName && touched.fullName ? (
                        <div className={styles.error_msg}>
                          {errors.fullName}
                        </div>
                      ) : null}
                    </div>
                  </Grid>

                  <Grid item xs={12} sm={12} md={12}>
                    <div className={styles.form_group}>
                      <label>
                        Email Address
                        <span className={styles.asterisk}>*</span>
                      </label>
                      <input
                        id="email"
                        name="email"
                        placeholder="Type Your Email "
                        type="email"
                        value={values.email ?? ""}
                        onChange={(e) => {
                          setFieldValue("email", e.target.value);
                          touched.email = true;
                        }}
                      />
                      {errors.email && touched.email ? (
                        <div className={styles.error_msg}>{errors.email}</div>
                      ) : null}
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={12} md={12}>
                    <div className={styles.form_group}>
                      <label>
                        Phone Number
                        <span className={styles.asterisk}>*</span>
                      </label>
                      <input
                        id="contactNumber"
                        name="contactNumber"
                        placeholder="Type Your Phone Number"
                        type="number"
                        value={values.contactNumber ?? ""}
                        onChange={(e) => {
                          setFieldValue("contactNumber", e.target.value);
                          touched.contactNumber = true;
                        }}
                      />
                      {errors.contactNumber && touched.contactNumber ? (
                        <div className={styles.error_msg}>
                          {errors.contactNumber}
                        </div>
                      ) : null}
                    </div>
                  </Grid>

                  <Grid item xs={12} sm={12} md={12}>
                    <div className={styles.form_group}>
                      <label>
                        Your City <span className={styles.asterisk}>*</span>
                      </label>
                      <input
                        id="cityName"
                        name="cityName"
                        placeholder="Type Your City"
                        type="text"
                        value={values.cityName ?? ""}
                        onChange={(e) => {
                          setFieldValue("cityName", e.target.value);
                          touched.cityName = true;
                        }}
                      />
                      {errors.cityName && touched.cityName ? (
                        <div className={styles.error_msg}>
                          {errors.cityName}
                        </div>
                      ) : null}
                    </div>
                  </Grid>

                  <Grid item xs={12} sm={12} md={12}>
                    <div className={styles.form_group}>
                      <label>
                        Which Country You would like to Study in Abroad?
                      </label>
                      <Select
                        instanceId="countryName"
                        styles={selectStyles}
                        placeholder="Please Select"
                        options={countryArr ?? []}
                        name="countryName"
                        onChange={(item) => {
                          setFieldValue("countryName", item.value);
                          touched.countryName = true;
                        }}
                        value={
                          countryArr
                            ? countryArr.filter(
                                (item) => item.value == values?.countryName
                              )
                            : {}
                        }
                      />
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <div className={styles.form_group}>
                      <label>IELTS Module</label>
                      <Select
                        instanceId="ieltsModule"
                        styles={selectStyles}
                        placeholder="Please Select"
                        options={ieltsModuleArr ?? []}
                        name="ieltsModule"
                        onChange={(item) => {
                          setFieldValue("ieltsModule", item.value);
                          touched.ieltsModule = true;
                        }}
                        value={
                          ieltsModuleArr
                            ? ieltsModuleArr.filter(
                                (item) => item.value == values?.ieltsModule
                              )
                            : {}
                        }
                      />
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <div className={styles.form_group}>
                      <label>Current Education</label>
                      <Select
                        instanceId="currentEducation"
                        styles={selectStyles}
                        placeholder="Please Select"
                        options={educationArr ?? []}
                        name="currentEducation"
                        onChange={(item) => {
                          setFieldValue("currentEducation", item.value);
                          touched.currentEducation = true;
                        }}
                        value={
                          educationArr
                            ? educationArr.filter(
                                (item) => item.value == values?.currentEducation
                              )
                            : {}
                        }
                      />
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <div className={styles.form_group}>
                      <label>
                        In which Year you would like to Study Abroad?
                      </label>
                      <Select
                        instanceId="year"
                        styles={selectStyles}
                        placeholder="Please Select"
                        options={yearArr ?? []}
                        name="year"
                        onChange={(item) => {
                          setFieldValue("year", item.value);
                          touched.year = true;
                        }}
                        value={
                          yearArr
                            ? yearArr.filter(
                                (item) => item.value == values?.year
                              )
                            : {}
                        }
                      />
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <div className={styles.form_group}>
                      <label>How would you fund your education?</label>
                      <Select
                        instanceId="funding"
                        styles={selectStyles}
                        placeholder="Please Select"
                        options={fundArr ?? []}
                        name="funding"
                        onChange={(item) => {
                          setFieldValue("funding", item.value);
                          touched.funding = true;
                        }}
                        value={
                          fundArr
                            ? fundArr.filter(
                                (item) => item.value == values?.funding
                              )
                            : {}
                        }
                      />
                    </div>
                  </Grid>
                </Grid>

                <div className={styles.checkbox_container_wrapper}>
                  <div className={styles.checkbox_with_error_wrapper}>
                    <div className={styles.checkbox_wrapper}>
                      <label className={styles.checkbox_lebel} html="terms">
                        <Field
                          id="terms"
                          type="checkbox"
                          name="terms_and_condition"
                          className="checkbox disable-team team_values"
                          value={true}
                          checked={
                            values.terms_and_condition &&
                            values.terms_and_condition === true
                              ? true
                              : false
                          }
                          onChange={(event) => {
                            setFieldValue(
                              "terms_and_condition",
                              !values.terms_and_condition
                            );
                            touched.terms_and_condition = true;
                          }}
                        />
                        <span style={{ marginLeft: "10px" }}>
                          I agree to UniSearch
                        </span>
                      </label>
                      <Link legacyBehavior href="/terms-and-conditions">
                        <a className={styles.term_link}>Terms</a>
                      </Link>
                      <span>and</span>
                      <Link legacyBehavior href="/privacy-policy">
                        <a className={styles.term_link}>Privacy Policy.</a>
                      </Link>
                    </div>

                    {errors.terms_and_condition &&
                    touched.terms_and_condition ? (
                      <div className={styles.error_msg}>
                        {errors.terms_and_condition}
                      </div>
                    ) : null}
                  </div>
                </div>

                <button
                  type="submit"
                  className={`${
                    styles.register_btn
                  } btn btn-secondary btn-bg btn-full ${
                    loading || isFormInable(values, errors)
                      ? styles.disabled
                      : "enable"
                  } `}
                  onClick={handleSubmit}
                  disabled={
                    loading || isFormInable(values, errors) ? true : false
                  }
                >
                  {loading && <CircularProgress size="16px" color="inherit" />}
                  Register Now!
                </button>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </section>
  );
};

export default LeadFormSection;
